import axios from "axios";
import { TCartProduct, TProduct } from "components/cart/cart-provider";
import { Recipe } from "types/recipe";
import { DietType } from "components/recipe/DietTypeFilter";

const API_URL = process.env.REACT_APP_API_URL + "/api";

/**
 * Detect diet types from search query
 */
export const detectDietTypesFromQuery = (query: string): DietType[] => {
  const lowerQuery = query.toLowerCase();
  const detectedTypes: DietType[] = [];

  // Vegan detection
  const veganKeywords = [
    "vegan",
    "vegane",
    "veganer",
    "veganes",
    "veganen",
    "no meat",
    "kein fleisch",
    "ohne fleisch",
    "fleischlos",
    "plant based",
    "pflanzlich",
    "pflanzliche",
    "pflanzlicher",
    "animal free",
    "tierfrei",
    "tierfreie",
    "tierfreier",
  ];

  // Vegetarian detection
  const vegetarianKeywords = [
    "vegetarisch",
    "vegetarische",
    "vegetarischer",
    "vegetarisches",
    "vegetarian",
    "vegetarisch",
    "fleischlos",
    "ohne fleisch",
    "no meat",
    "kein fleisch",
    "fleischfrei",
  ];

  // Gluten free detection
  const glutenFreeKeywords = [
    "glutenfrei",
    "gluten free",
    "ohne gluten",
    "kein gluten",
    "glutenlos",
    "glutenfreie",
    "glutenfreier",
    "glutenfreies",
    "celiac",
    "zöliakie",
    "zöliakie",
    "weizenfrei",
    "weizen free",
  ];

  // Check for vegan keywords
  if (veganKeywords.some((keyword) => lowerQuery.includes(keyword))) {
    detectedTypes.push("vegan");
  }

  // Check for vegetarian keywords (if not already vegan)
  if (
    !detectedTypes.includes("vegan") &&
    vegetarianKeywords.some((keyword) => lowerQuery.includes(keyword))
  ) {
    detectedTypes.push("vegetarisch");
  }

  // Check for gluten free keywords
  if (glutenFreeKeywords.some((keyword) => lowerQuery.includes(keyword))) {
    detectedTypes.push("glutenfrei");
  }

  return detectedTypes;
};

export const searchRecipesForCategory = async (
  categoryId: string | string[],
  options?: { serviceId: string; dietTypes?: DietType[]; query?: string }
) => {
  // Provide default serviceId if undefined
  const serviceId = options?.serviceId || "billa";

  // Auto-detect diet types from query if not provided
  let dietTypes = options?.dietTypes || [];
  if (!dietTypes.length && options?.query) {
    dietTypes = detectDietTypesFromQuery(options.query);
  }

  // Build query parameters
  const params = new URLSearchParams();
  params.append("serviceId", serviceId);

  // Add diet types if provided
  if (dietTypes.length > 0) {
    dietTypes.forEach((dietType) => {
      params.append("dietTypes", dietType);
    });
  }

  // Handle multiple categories or single category
  let url = "";
  if (Array.isArray(categoryId)) {
    // Use new multi-category endpoint
    categoryId.forEach((cat) => params.append("categories", cat));
    url = API_URL + "/search/recipes?" + params.toString();
  } else {
    // Use existing single-category endpoint
    url = API_URL + "/search/category/" + categoryId + "?" + params.toString();
  }

  const { data } = await axios.get<Recipe[]>(url);
  return data;
};

/**
 * Generic search function with retry and rephrasing capability
 */
export const searchWithRetry = async <T extends TProduct[] | Recipe[]>({
  query,
  context,
  retryCount = 3,
  searchFunction,
  shouldPick = true,
  options,
}: {
  query: string;
  context: string;
  retryCount?: number;
  searchFunction: (
    query: string,
    options: { serviceId: string }
  ) => Promise<T> | T;
  shouldPick?: boolean;
  options: { serviceId: string };
}): Promise<{
  searchResults: T;
  decision: any;
  success: boolean;
}> => {
  if (retryCount <= 0) {
    return {
      searchResults: [] as Awaited<T>,
      decision: null,
      success: false,
    };
  }

  let searchResults = await searchFunction(query, options);

  // Try rephrasing if no results
  if (searchResults?.length === 0) {
    const rephrased = await axios.post(API_URL + "/rephrase", {
      query: query,
      context: context,
    });

    searchResults = await searchFunction(rephrased.data.query, options);
  }

  // Get decision about which products to pick
  const { data: decision } = shouldPick
    ? await axios.post(API_URL + "/pick", {
        items: searchResults,
        query: query,
        context: context,
      })
    : { data: { success: true } };

  // If decision failed but we have retries left, rephrase and try again
  if (!decision.success && retryCount > 1) {
    const rephrased = await axios.post(API_URL + "/rephrase", {
      query: query,
      context: context,
    });

    return searchWithRetry({
      query: rephrased.data.query,
      context,
      retryCount: retryCount - 1,
      searchFunction,
      options,
    });
  }

  return { searchResults, decision, success: decision.success };
};

/**
 * Search for recipes with retry and rephrasing capability
 */
export const performRecipeSearch = async ({
  query,
  context,
  searchFunction,
  options,
}: {
  query: string;
  context: string;
  searchFunction: (
    query: string,
    options: { serviceId: string }
  ) => Promise<Recipe[]>;
  options: { serviceId: string };
}): Promise<Recipe[]> => {
  const { searchResults } = await searchWithRetry<Recipe[]>({
    query,
    context,
    retryCount: 3,
    searchFunction,
    shouldPick: false,
    options,
  });

  return searchResults;
};

/**
 * Process items in batches using a queue system
 */
async function processBatchQueue<T, R>({
  items,
  batchSize = 5,
  processItem,
}: {
  items: T[];
  batchSize?: number;
  processItem: (item: T) => Promise<R | void>;
}): Promise<void> {
  const queue = [...items];

  while (queue.length > 0) {
    const batch = queue.splice(0, batchSize);
    // Process this batch concurrently
    await Promise.allSettled(batch.map((item) => processItem(item)));

    // Optional: Add a small delay between batches to prevent overwhelming the server
    if (queue.length > 0) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }
}

/**
 * Search for products with retry and rephrasing capability
 * Processes items in batches of 5 at a time to prevent overwhelming the server
 */
export const performProductSearch = async ({
  items,
  context,
  searchFunction,
  batchSize = 5,
  serviceId,
}: {
  items: { name: string }[];
  context: string;
  searchFunction: (
    query: string,
    options: { serviceId: string }
  ) => Promise<TProduct[]> | TProduct[];
  batchSize?: number;
  serviceId: string;
}): Promise<TCartProduct[]> => {
  const newCart: TCartProduct[] = [];

  const queryItem = async (item: { name: string }): Promise<void> => {
    const { searchResults, decision, success } = await searchWithRetry({
      query: item.name,
      context,
      retryCount: 3,
      searchFunction,
      options: { serviceId },
    });

    if (success && decision.result) {
      newCart.push(
        ...decision.result.map((product: TProduct) => ({
          product,
          quantity: 1,
          options: searchResults.filter((result) => result.id !== product.id),
        }))
      );
    } else {
    }
  };

  // Process items in batches using the queue system
  await processBatchQueue({
    items,
    batchSize,
    processItem: queryItem,
  });

  return newCart;
};
